import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TypedMemo } from '../../../common/types';
import { DeltaPill } from '../../components/primitives/DeltaPill';
import { platformMeta, platformSoftFill } from '../platformMeta';
import { SerializedSocialChannel } from '../types';
import { FollowersPillKind } from './channelNumbersState';
import { channelNumbersMessages as messages } from './channelNumbersMessages';
import { PanelHead } from '../../components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './PromoteChannels.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ChannelCardHeadProps {
  channel: SerializedSocialChannel;
  /** The handle is a button that opens the handle field. False for a connected channel. */
  canEditHandle: boolean;
  pill: FollowersPillKind;
  onEditHandle: () => void;
}

/** The head row of a channel card (PanelHead): the mark, the name, the handle and the change pill. */
const ChannelCardHeadComponent: React.FC<ChannelCardHeadProps> = ({ channel, canEditHandle, pill, onEditHandle }) => {
  const intl = useIntl();
  const meta = platformMeta[channel.platform];
  const hasHandle = channel.handle !== null && channel.handle !== '';

  return (
    <PanelHead
      lead={
        <span className={styles.mark} style={{ background: platformSoftFill(channel.platform), color: meta.color }}>
          <FontAwesomeIcon icon={meta.icon} />
        </span>
      }
      title={meta.name}
      subtitle={
        canEditHandle ? (
          <button
            type="button"
            className={styles.handle}
            title={intl.formatMessage({ id: 'app.sml.promote.channel.edit', defaultMessage: 'Edit handle' })}
            onClick={onEditHandle}
          >
            {channel.handle}
          </button>
        ) : (
          <span className={styles.handle}>
            {hasHandle
              ? channel.handle
              : intl.formatMessage({ id: 'app.sml.promote.channel.notConnected', defaultMessage: 'not connected' })}
          </span>
        )
      }
      right={
        <span className={styles.headDelta} data-testid="channel-followers-change">
          {pill === 'change' && channel.followersChange !== null ? <DeltaPill value={channel.followersChange} /> : null}
          {pill === 'firstWeek' ? <DeltaPill value={0} label={intl.formatMessage(messages.firstWeek)} /> : null}
        </span>
      }
    />
  );
};

export const ChannelCardHead = TypedMemo(ChannelCardHeadComponent);
